"use client";

import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { useConnectionSignatures } from "@/lib/hooks/api/emails/email-signatures";

export interface AutoSignatureSelectorProps {
  form: UseFormReturn<any>;
  isOpen: boolean;
  connectionId?: string;
}

/**
 * Hook that automatically selects the default signature for an email connection
 * when the compose modal opens or the connection changes.
 */
export function useAutoSignatureSelector({
  form,
  isOpen,
  connectionId,
}: AutoSignatureSelectorProps) {
  // Fetch signatures for the selected email connection
  const { data: connectionSignatures = [] } = useConnectionSignatures(
    connectionId || null,
    isOpen // Only fetch when modal is open
  );

  // Auto-set default signature when connection changes or modal opens
  useEffect(() => {
    if (!isOpen || !connectionId || connectionSignatures.length === 0) return;

    // Find the default signature for this connection
    const defaultSignature = connectionSignatures.find((sig) => sig.is_default);

    if (defaultSignature && !form.getValues("signature")) {
      // Only set if no signature is currently selected
      form.setValue("signature", defaultSignature.id);

      // Note: Variable context integration would go here if needed
      // For now, the signature selector will handle the content display
    }
  }, [isOpen, connectionId, connectionSignatures, form]);

  return {
    connectionSignatures,
    hasSignatures: connectionSignatures.length > 0,
    hasDefaultSignature: connectionSignatures.some((sig) => sig.is_default),
  };
}
